import { useNavigate } from 'react-router-dom';
import { WaterfallStream, WaterBackground } from '../components/Backgrounds';

// SVG Icons for features - this avoids needing an icon library
const BrainIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-white"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.97-3.44 2.5 2.5 0 0 1-2.02-4.5 2.5 2.5 0 0 1 2.02-4.5 2.5 2.5 0 0 1 2.97-3.44A2.5 2.5 0 0 1 9.5 2Z" /><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.97-3.44 2.5 2.5 0 0 0 2.02-4.5 2.5 2.5 0 0 0-2.02-4.5 2.5 2.5 0 0 0-2.97-3.44A2.5 2.5 0 0 0 14.5 2Z" /></svg>
);

const TargetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-white"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>
);

const BarChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-white"><line x1="12" x2="12" y1="20" y2="10" /><line x1="18" x2="18" y1="20" y2="4" /><line x1="6" x2="6" y1="20" y2="16" /></svg>
);

// Define a type for our particle for type safety



// Main Landing Page Component
export const LandingPage = () => {
  const navigate = useNavigate();

  return (
    // --- CHANGE 2: Changed min-h-[70vh] to min-h-screen for a full-height background
    <div className="relative min-h-screen bg-gradient-to-b from-blue-500 via-blue-600 to-blue-800 text-white overflow-x-hidden font-sans">
      <WaterBackground />
      {/* --- CHANGE 3: Moved <WaterfallStream /> here to be a top-level background element */}
      <WaterfallStream />

      <div className="relative z-10">
        <header className="absolute top-0 left-0 w-full z-40 py-2 px-6 md:px-12 flex justify-between items-center bg-blue-800/25 backdrop-blur-sm">
          <div className="flex items-center">
            <div className="flex items-center bg-blue-700/30 backdrop-blur-sm rounded-full px-3 py-1">
              <div className="flex items-center justify-center bg-white rounded-full p-1 mr-2">
                <img src="/hydrio-icon.png" alt="Hydrio Logo" className="h-6 w-6" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">Hydrio</h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="bg-white/20 text-white font-semibold px-4 py-2 rounded-lg hover:bg-white/30 transition-colors border border-white/30 backdrop-blur-sm"
          >
            Login / Signup
          </button>
        </header>

        {/* Hero Section now starts from the top of the viewport */}
        {/* --- NOTE: <WaterfallStream /> was removed from inside this div */}
        <div className="relative min-h-[70vh] text-white overflow-x-hidden font-sans">
          <main className="container mx-auto px-6 pt-30 pb-12 md:pt-80 md:pb-20 text-center relative z-20">
            <h2 className="text-5xl md:text-7xl font-extrabold leading-tight mb-40">
              Intelligent Hydration Unlocked.
            </h2>
            <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8 text-blue-200">
              The smart water reminder that works with you automatically. No more manual logging. Stop guessing, start thriving.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-white text-blue-700 font-bold text-lg px-8 py-4 rounded-xl shadow-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 mb-5"
            >
              Get Started for Free
            </button>
            <div className="mt-10 flex justify-center">
              <div className="animate-bounce">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </main>
        </div>

        {/* Features Section */}
        <section
          id="features"
          className="relative bg-cover bg-fixed"
          style={{ backgroundImage: "url('/21-swan-SEOPVz1NAgw-unsplash.jpg')" }}
        >
          {/* Blue to transparent gradient overlay at the TOP */}
          <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-blue-800 to-transparent"></div>

          {/* Main Overlay */}
          <div className="absolute inset-0 bg-blue-700/50 backdrop-blur-sm"></div>

          {/* Transparent to blue gradient overlay at the BOTTOM */}
          <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-blue-800 to-transparent"></div>

          {/* Content */}
          <div className="relative z-30 py-20">
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold">Why Hydrio?</h3>
                <p className="text-blue-200 mt-2">More than just a reminder. It's your personal hydration coach.</p>
              </div>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-500/80 mx-auto mb-4">
                    <BrainIcon />
                  </div>
                  <h4 className="text-xl font-semibold mb-2">AI-Powered Plans</h4>
                  <p className="text-blue-200">Our smart algorithm adapts your hydration goals in real-time. Just finished a workout? We'll adjust your plan.</p>
                </div>
                <div className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-500/80 mx-auto mb-4">
                    <TargetIcon />
                  </div>
                  <h4 className="text-xl font-semibold mb-2">Personalized Goals</h4>
                  <p className="text-blue-200">Your body is unique. We calculate your baseline needs and help you build a consistent, healthy habit.</p>
                </div>
                <div className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-500/80 mx-auto mb-4">
                    <BarChartIcon />
                  </div>
                  <h4 className="text-xl font-semibold mb-2">Track Your Progress</h4>
                  <p className="text-blue-200">Visualize your success with beautiful charts and insights. Stay motivated by seeing how far you've come.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold">Get Started in Seconds</h3>
            </div>
            <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16">
              <div className="text-center max-w-xs p-4">
                <div className="text-5xl font-extrabold text-blue-300 mb-2">1</div>
                <h4 className="text-xl font-semibold mb-2">Create Your Profile</h4>
                <p className="text-blue-200">Tell us a little about yourself and your lifestyle.</p>
              </div>
              <div className="text-blue-300 hidden md:block text-2xl">→</div>
              <div className="text-center max-w-xs p-4">
                <div className="text-5xl font-extrabold text-blue-300 mb-2">2</div>
                <h4 className="text-xl font-semibold mb-2">Get Your AI Plan</h4>
                <p className="text-blue-200">Receive your custom hydration schedule instantly.</p>
              </div>
              <div className="text-blue-300 hidden md:block text-2xl">→</div>
              <div className="text-center max-w-xs p-4">
                <div className="text-5xl font-extrabold text-blue-300 mb-2">3</div>
                <h4 className="text-xl font-semibold mb-2">Stay Hydrated</h4>
                <p className="text-blue-200">Log your intake and let our smart reminders do the rest.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-blue-800/40 backdrop-blur-sm py-6 border-t border-white/10">
          <div className="container mx-auto px-6 text-center text-blue-200">
            <div className="flex justify-center items-center mb-2">
              <div className="flex items-center bg-blue-700/30 backdrop-blur-sm rounded-full px-2 py-1 mr-2">
                <div className="flex items-center justify-center bg-white rounded-full p-1 mr-1">
                  <img src="/hydrio-icon.png" alt="Hydrio Logo" className="h-4 w-4" />
                </div>
                <span className="font-bold text-sm">Hydrio</span>
              </div>
              <p>&copy; {new Date().getFullYear()} Hydrio. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
